class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Celebrate freshly-added bundles once per session
    try {
      const sessionKey = 'uw-cart-celebrated-bundles';
      const seen = new Set(JSON.parse(sessionStorage.getItem(sessionKey) || '[]'));
      const bundleCards = this.querySelectorAll('.cart-line-item--bundle');
      bundleCards.forEach((card) => {
        const key = card.dataset.lineKey;
        if (!key || seen.has(key)) return;
        card.setAttribute('data-celebrate', 'true');
        setTimeout(() => card.removeAttribute('data-celebrate'), 700);
        seen.add(key);
      });
      sessionStorage.setItem(sessionKey, JSON.stringify([...seen]));
    } catch (_) {
      // sessionStorage failure is non-fatal (private browsing on some browsers)
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ============================================================
   Cart interaction handlers — event-delegated on document so they
   survive section re-renders. Resolves "host" at event time so
   the same handlers cover both drawer and cart page.

   click  → remove text link, cross-sell add (buttons)
   change → iOS-style subscribe switch, frequency dropdown (form controls)
   ============================================================ */
document.addEventListener('click', async (e) => {
  if (!window.cartActions) return;
  const host =
    e.target.closest('cart-drawer-items, cart-items') ||
    document.querySelector('cart-drawer-items') ||
    document.querySelector('cart-items');
  if (!host) return;

  // Line remove (text link)
  const removeBtn = e.target.closest('.cart-line-item__remove[data-action="remove"]');
  if (removeBtn) {
    e.preventDefault();
    const card = removeBtn.closest('.cart-line-item');
    const lineKey = card && card.dataset.lineKey;
    if (!lineKey) return;
    if (card) card.style.opacity = '0.5';
    try {
      await window.cartActions.removeLine(host, lineKey);
    } catch (err) {
      console.error('Remove failed:', err);
      if (card) card.style.opacity = '1';
    }
    return;
  }

  // Recommendation add (carousel cards + the maple upsell card, which
  // use different wrapper classes but share the data-action contract).
  const addBtn = e.target.closest('[data-action="add-recommendation"]');
  if (addBtn) {
    const variantId = addBtn.dataset.variantId;
    if (!variantId) return;
    addBtn.disabled = true;
    const originalText = addBtn.textContent;
    addBtn.textContent = '…';
    try {
      await window.cartActions.addCrossSellItem(host, variantId);
    } catch (err) {
      console.error('Recommendation add failed:', err);
      addBtn.disabled = false;
      addBtn.textContent = originalText;
    }
    return;
  }

  // Recommendations carousel arrows
  const arrowBtn = e.target.closest('.cart-recommendations__arrow');
  if (arrowBtn) {
    const direction = arrowBtn.dataset.action === 'recs-next' ? 1 : -1;
    const section = arrowBtn.closest('.cart-recommendations');
    if (!section) return;
    const track = section.querySelector('[data-recs-track]');
    if (!track) return;
    const card = track.querySelector('.cart-recommendations__card');
    const cardWidth = card ? card.getBoundingClientRect().width + 12 : 200;
    track.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
    return;
  }
});

// Subscribe toggle switch + frequency dropdown (change events)
document.addEventListener('change', async (e) => {
  if (!window.cartActions) return;
  const host =
    e.target.closest('cart-drawer-items, cart-items') ||
    document.querySelector('cart-drawer-items') ||
    document.querySelector('cart-items');
  if (!host) return;

  // iOS-style subscribe switch
  const toggleInput = e.target.closest('.cart-toggle__input');
  if (toggleInput) {
    const lineKey = toggleInput.dataset.lineKey;
    const planId = toggleInput.dataset.sellingPlanId;
    // change has already flipped .checked; previous state is the opposite
    const wasChecked = !toggleInput.checked;
    if (!lineKey) return;
    // Reveal/hide the frequency row immediately so it doesn't lag behind the
    // cart round-trip. The re-rendered line (which also carries the subscribed
    // state) replaces this a moment later; on failure we roll it back.
    const lineItem = toggleInput.closest('.cart-line-item');
    if (lineItem) lineItem.classList.toggle('cart-line-item--subscribed', toggleInput.checked);
    try {
      if (toggleInput.checked) {
        await window.cartActions.upgradeLineToSubscription(host, lineKey, planId);
      } else {
        await window.cartActions.downgradeLineToOneTime(host, lineKey);
      }
    } catch (err) {
      console.error('Subscribe toggle failed:', err);
      toggleInput.checked = wasChecked;
      if (lineItem) lineItem.classList.toggle('cart-line-item--subscribed', wasChecked);
    }
    return;
  }

  // Frequency dropdown
  const freqSelect = e.target.closest('.cart-frequency');
  if (freqSelect && freqSelect.tagName === 'SELECT') {
    const lineKey = freqSelect.dataset.lineKey;
    const planId = freqSelect.value;
    if (!lineKey || !planId) return;
    const previousValue = freqSelect.dataset.previousValue;
    try {
      await window.cartActions.upgradeLineToSubscription(host, lineKey, planId);
      freqSelect.dataset.previousValue = planId;
    } catch (err) {
      console.error('Frequency change failed:', err);
      if (previousValue) freqSelect.value = previousValue;
    }
    return;
  }
});
